'use client'

import React, { useState } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Star, Calendar, MapPin, DollarSign, FileText, Tag, Info, Sparkles, Lock, Building2, ExternalLink, Link as LinkIcon, User, Mail, Phone } from 'lucide-react'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

export interface PreviewOpportunity {
  id: string | number
  title: string
  agency: string | null
  locationState: string | null
  sourceType?: string | null
  status?: string | null
  naicsCode?: string | null
  value: string | null
  isEstimate?: boolean
  dueDate: string | null
  posted_date: string | null
  aiDescription?: string | null
  category?: string | null
  description?: string | null
  // Additional fields for full details (federal)
  solicitation_number?: string | null
  setAside?: string | null
  ui_link?: string | null
  officeCity?: string | null
  primaryNaicsCode?: string | null
  classificationCode?: string | null
  resourceLinks?: string[] | null
  noticeLinks?: any[] | null
  additionalInfoLink?: string | null
  pointsOfContact?: any[] | null
  // Grant-specific fields
  fundingType?: string | null
  awardCeiling?: string | null
  awardFloor?: string | null
  expectedAwards?: string | null
  costSharing?: string | null
  eligibleApplicants?: any[] | null
  cfdaNumbers?: any[] | null
  grantSource?: string | null
  phase?: string | null
  program?: string | null
  topicNumber?: string | null
  topicTitle?: string | null
  topicDescription?: string | null
  grantorContactName?: string | null
  grantorContactEmail?: string | null
  grantorContactPhone?: string | null
  // SLED enriched fields
  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
  sledAgency?: string | null
  sledSolicitationNumber?: string | null
}

interface PreviewOpportunityCardProps {
  opportunity: PreviewOpportunity
  contractLevel: 'federal' | 'sled' | 'grants'
}

// Helper function to format dates (short)
const formatDateString = (dateString: string | null): string => {
  if (!dateString) return 'TBD'
  try {
    let dateToParse = dateString
    if (!dateString.includes(' ') && !dateString.includes('T')) {
      dateToParse = dateString + 'T00:00:00Z'
    }
    const date = new Date(dateToParse)
    if (isNaN(date.getTime())) return 'TBD'
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    })
  } catch {
    return 'TBD'
  }
}

// Helper function to format dates (long, for details modal)
const formatDateStringLong = (dateString: string | null): string => {
  if (!dateString) return 'N/A'
  try {
    let dateToParse = dateString
    if (!dateString.includes(' ') && !dateString.includes('T')) {
      dateToParse = dateString + 'T00:00:00Z'
    }
    const date = new Date(dateToParse)
    if (isNaN(date.getTime())) return 'N/A'
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    })
  } catch {
    return 'N/A'
  }
}

export function PreviewOpportunityCard({ 
  opportunity, 
  contractLevel
}: PreviewOpportunityCardProps) {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false)

  const formatCurrency = (value: string | null) => {
    if (!value || value === 'N/A') return null
    try {
      const numValue = Number(value)
      if (isNaN(numValue)) return null
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD', 
        maximumFractionDigits: 0 
      }).format(numValue)
    } catch {
      return null
    }
  }

  // Remove URLs from title text
  const cleanTitle = (title: string | null): string => {
    if (!title) return 'Untitled Opportunity'
    return title
      .replace(/https?:\/\/[^\s]+/gi, '')
      .replace(/www\.[^\s]+/gi, '')
      .replace(/\s+/g, ' ')
      .trim()
  }

  const formattedValue = formatCurrency(opportunity.value)
  const formattedDueDate = formatDateString(opportunity.dueDate)
  const formattedPostedDate = formatDateString(opportunity.posted_date)
  const formattedDueDateLong = formatDateStringLong(opportunity.dueDate)
  const formattedPostedDateLong = formatDateStringLong(opportunity.posted_date)
  const displayTitle = cleanTitle(opportunity.title)

  return (
    <>
      <Card className="h-full hover:shadow-lg transition-all duration-200 border-border/50 group cursor-pointer" onClick={() => setIsDetailsModalOpen(true)}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2 mb-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <h3 className="text-base font-semibold text-foreground line-clamp-2 flex-1">
                        {displayTitle}
                      </h3>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">{displayTitle}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 rounded-full hover:bg-yellow-500/10 opacity-60 hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsTrialModalOpen(true)
                  }}
                  aria-label="Sign up to track"
                >
                  <Star className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
              
              {/* AI Description placeholder */}
              {opportunity.aiDescription ? (
                <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                  {opportunity.aiDescription}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed italic">
                  Sign up to view AI-generated insights and full details
                </p>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* Badges Row */}
          <div className="flex flex-wrap items-center gap-2">
            {contractLevel === 'federal' && opportunity.status && (
              <Badge variant="outline" className="text-xs">
                {opportunity.status}
              </Badge>
            )}
            {contractLevel === 'sled' && opportunity.sourceType && (
              <Badge 
                variant="outline" 
                className={cn(
                  "text-xs",
                  opportunity.sourceType === 'STATE' && 'bg-blue-500/10 text-blue-700 border-blue-500/20',
                  opportunity.sourceType === 'LOCAL' && 'bg-green-500/10 text-green-700 border-green-500/20',
                  opportunity.sourceType === 'EDUCATION' && 'bg-purple-500/10 text-purple-700 border-purple-500/20'
                )}
              >
                {opportunity.sourceType}
              </Badge>
            )}
            {contractLevel === 'grants' && opportunity.grantSource && (
              <Badge variant="outline" className={cn("text-xs", opportunity.grantSource === 'sbir.gov' ? 'bg-violet-500/10 text-violet-700 border-violet-500/20' : 'bg-teal-500/10 text-teal-700 border-teal-500/20')}>
                {opportunity.grantSource === 'sbir.gov' ? 'SBIR/STTR' : 'Grants.gov'}
              </Badge>
            )}
            {contractLevel === 'grants' && opportunity.status && (
              <Badge variant="outline" className="text-xs">{opportunity.status}</Badge>
            )}
            {contractLevel === 'grants' && opportunity.phase && (
              <Badge variant="outline" className="text-xs bg-indigo-500/10 text-indigo-700 border-indigo-500/20">{opportunity.phase}</Badge>
            )}
            {contractLevel === 'grants' && opportunity.fundingType && (
              <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-700 border-blue-500/20">{opportunity.fundingType}</Badge>
            )}
            {opportunity.category && (
              <Badge variant="outline" className="text-xs">
                <Tag className="h-3 w-3 mr-1" />
                {opportunity.category}
              </Badge>
            )}
            {contractLevel === 'sled' && opportunity.sledSolicitationNumber && (
              <Badge variant="outline" className="text-xs font-mono">
                {opportunity.sledSolicitationNumber}
              </Badge>
            )}
          </div>

          {/* Key Information Grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            {/* Value */}
            {formattedValue && (
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-emerald-600 shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Est. Value</div>
                  <div className="font-semibold text-emerald-600 truncate">
                    {formattedValue}
                  </div>
                </div>
              </div>
            )}

            {/* Due Date */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-600 shrink-0" />
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">Due Date</div>
                <div className={cn(
                  "font-medium text-xs",
                  formattedDueDate === 'TBD' ? "text-muted-foreground italic" : "text-foreground"
                )}>
                  {formattedDueDate}
                </div>
              </div>
            </div>

            {/* Posted Date */}
            {opportunity.posted_date && formattedPostedDate !== 'TBD' && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Posted</div>
                  <div className="font-medium text-muted-foreground text-xs">
                    {formattedPostedDate}
                  </div>
                </div>
              </div>
            )}

            {/* Location */}
            {opportunity.locationState && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Location</div>
                  <div className="font-medium text-foreground text-xs truncate">
                    {opportunity.locationState}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Additional Info Row */}
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border/50 text-xs text-muted-foreground">
            {opportunity.naicsCode && (
              <div className="flex items-center gap-1">
                <span className="font-mono bg-muted/50 px-1.5 py-0.5 rounded">
                  NAICS: {opportunity.naicsCode}
                </span>
              </div>
            )}
            {opportunity.agency && (
              <div className="flex items-center gap-1 truncate">
                <FileText className="h-3 w-3 shrink-0" />
                <span className="truncate">{opportunity.agency}</span>
              </div>
            )}
            {contractLevel === 'sled' && opportunity.sledAgency && (
              <div className="flex items-center gap-1 truncate">
                <Building2 className="h-3 w-3 shrink-0" />
                <span className="truncate">{opportunity.sledAgency}</span>
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="mt-2">
            <Button
              variant="default"
              size="sm"
              className="w-full"
              onClick={(e) => {
                e.stopPropagation()
                setIsDetailsModalOpen(true)
              }}
            >
              <Info className="h-3.5 w-3.5 mr-2" />
              View Details
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Opportunity Details Modal */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <div className="flex items-start justify-between gap-4 pr-8">
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-xl font-bold">
                  {displayTitle}
                </DialogTitle>
                <DialogDescription className="mt-1">
                  Complete details and information about this opportunity
                </DialogDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-full hover:bg-yellow-500/10 transition-all duration-200"
                onClick={() => {
                  setIsDetailsModalOpen(false)
                  setTimeout(() => setIsTrialModalOpen(true), 200)
                }}
                aria-label="Sign up to track"
              >
                <Star className="h-5 w-5 text-muted-foreground hover:text-yellow-500 transition-colors" />
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-6">
            {/* Agency/Jurisdiction */}
            <div className="flex items-start gap-3">
              <Building2 className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-muted-foreground mb-1">Agency / Jurisdiction</div>
                <div className="text-base font-semibold">{opportunity.agency || 'N/A'}</div>
              </div>
            </div>

            {/* Description Section */}
            {(opportunity.description || opportunity.aiDescription || opportunity.title) && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Info className="h-4 w-4" />
                  Description
                </div>
                {(contractLevel === 'grants' || contractLevel === 'sled') && opportunity.description ? (
                  <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                      {opportunity.description}
                    </p>
                  </div>
                ) : opportunity.aiDescription ? (
                  <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
                    <div className="flex items-start gap-2 mb-2">
                      <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-xs font-semibold text-primary">AI-Generated Summary</span>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                      {opportunity.aiDescription}
                    </p>
                  </div>
                ) : (
                  <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
                    <p className="text-sm text-foreground leading-relaxed">
                      {opportunity.title || 'No description available.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2">
              {contractLevel === 'federal' && opportunity.status && (
                <Badge variant="outline" className="text-sm">
                  {opportunity.status}
                </Badge>
              )}
              {contractLevel === 'sled' && opportunity.sourceType && (
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-sm",
                    opportunity.sourceType === 'STATE' && 'bg-blue-500/10 text-blue-700 border-blue-500/20',
                    opportunity.sourceType === 'LOCAL' && 'bg-green-500/10 text-green-700 border-green-500/20',
                    opportunity.sourceType === 'EDUCATION' && 'bg-purple-500/10 text-purple-700 border-purple-500/20'
                  )}
                >
                  {opportunity.sourceType}
                </Badge>
              )}
              {contractLevel === 'sled' && opportunity.status && (
                <Badge variant="outline" className="text-sm">
                  {opportunity.status}
                </Badge>
              )}
              {contractLevel === 'federal' && opportunity.setAside && (
                <Badge variant="outline" className="text-sm bg-amber-500/10 text-amber-700 border-amber-500/30">
                  {opportunity.setAside}
                </Badge>
              )}
              {contractLevel === 'grants' && opportunity.grantSource && (
                <Badge variant="outline" className={cn("text-sm", opportunity.grantSource === 'sbir.gov' ? 'bg-violet-500/10 text-violet-700 border-violet-500/20' : 'bg-teal-500/10 text-teal-700 border-teal-500/20')}>
                  {opportunity.grantSource === 'sbir.gov' ? 'SBIR/STTR' : 'Grants.gov'}
                </Badge>
              )}
              {contractLevel === 'grants' && opportunity.status && (
                <Badge variant="outline" className="text-sm">{opportunity.status}</Badge>
              )}
              {contractLevel === 'grants' && opportunity.phase && (
                <Badge variant="outline" className="text-sm bg-indigo-500/10 text-indigo-700 border-indigo-500/20">{opportunity.phase}</Badge>
              )}
              {contractLevel === 'grants' && opportunity.fundingType && (
                <Badge variant="outline" className="text-sm bg-blue-500/10 text-blue-700 border-blue-500/20">{opportunity.fundingType}</Badge>
              )}
              {opportunity.category && (
                <Badge variant="outline" className="text-sm">
                  <Tag className="h-3 w-3 mr-1" />
                  {opportunity.category}
                </Badge>
              )}
            </div>

            {/* Key Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Estimated Value */}
              {formattedValue && (
                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-muted-foreground mb-1">Estimated Value</div>
                    <div className="text-lg font-bold text-emerald-600">
                      {formattedValue}
                    </div>
                    {opportunity.isEstimate && (
                      <div className="text-xs text-muted-foreground italic mt-1">AI-Generated Estimate</div>
                    )}
                  </div>
                </div>
              )}

              {/* Due Date */}
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Due Date</div>
                  <div className={cn(
                    "text-base font-semibold",
                    formattedDueDateLong === 'N/A' ? "text-muted-foreground italic" : "text-foreground"
                  )}>
                    {formattedDueDateLong}
                  </div>
                </div>
              </div>

              {/* Posted Date */}
              {opportunity.posted_date && formattedPostedDateLong !== 'N/A' && (
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-muted-foreground mb-1">Posted Date</div>
                    <div className="text-base font-semibold text-foreground">
                      {formattedPostedDateLong}
                    </div>
                  </div>
                </div>
              )}

              {/* Location */}
              {opportunity.locationState && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-muted-foreground mb-1">Location</div>
                    <div className="text-base font-semibold text-foreground">
                      {opportunity.locationState}
                    </div>
                  </div>
                </div>
              )}

              {/* Solicitation Number (Federal) */}
              {contractLevel === 'federal' && opportunity.solicitation_number && (
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-muted-foreground mb-1">Solicitation Number</div>
                    <div className="text-base font-mono font-semibold text-foreground">
                      {opportunity.solicitation_number}
                    </div>
                  </div>
                </div>
              )}

              {/* Solicitation Number (SLED) */}
              {contractLevel === 'sled' && opportunity.sledSolicitationNumber && (
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-muted-foreground mb-1">Solicitation Number</div>
                    <div className="text-base font-mono font-semibold text-foreground">
                      {opportunity.sledSolicitationNumber}
                    </div>
                  </div>
                </div>
              )}

              {/* NAICS Code */}
              {opportunity.naicsCode && (
                <div className="flex items-start gap-3">
                  <Tag className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-muted-foreground mb-1">NAICS Code</div>
                    <div className="text-base font-mono font-semibold text-foreground">
                      {opportunity.naicsCode}
                    </div>
                  </div>
                </div>
              )}

              {/* Source URL */}
              {opportunity.ui_link && (
                <div className="flex items-start gap-3">
                  <ExternalLink className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-muted-foreground mb-1">Source URL</div>
                    <a 
                      href={opportunity.ui_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-base font-semibold text-primary hover:underline break-all"
                    >
                      {opportunity.ui_link}
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Details Section */}
            {opportunity.pointsOfContact && opportunity.pointsOfContact.length > 0 && (
              <div className="pt-4 border-t border-border/50">
                <div className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Contact Details
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {opportunity.pointsOfContact.map((contact: any, idx: number) => {
                    const name = contact.fullName || contact.name || null
                    const contactTitle = contact.title || null
                    const email = contact.email || null
                    const phone = contact.phone || contact.fax || null
                    const type = contact.type || null

                    if (!name && !email && !phone) return null

                    return (
                      <div
                        key={idx}
                        className="bg-muted/30 rounded-lg p-3 border border-border/50 space-y-1.5"
                      >
                        {type && (
                          <Badge variant="outline" className="text-[10px] font-medium mb-1">
                            {type}
                          </Badge>
                        )}
                        {name && (
                          <div className="flex items-center gap-2 text-sm">
                            <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="font-semibold">{name}</span>
                            {contactTitle && (
                              <span className="text-muted-foreground">— {contactTitle}</span>
                            )}
                          </div>
                        )}
                        {email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <a
                              href={`mailto:${email}`}
                              className="text-primary hover:underline break-all"
                            >
                              {email}
                            </a>
                          </div>
                        )}
                        {phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <a
                              href={`tel:${phone}`}
                              className="text-primary hover:underline"
                            >
                              {phone}
                            </a>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Documents / Links Section */}
            {((opportunity.resourceLinks && opportunity.resourceLinks.length > 0) || 
              (opportunity.noticeLinks && opportunity.noticeLinks.length > 0) || 
              opportunity.additionalInfoLink) && (
              <div className="pt-4 border-t border-border/50">
                <div className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" />
                  Documents & Links
                </div>
                <div className="flex flex-wrap gap-2">
                  {opportunity.additionalInfoLink && (
                    <a
                      href={opportunity.additionalInfoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 min-h-[2rem] py-1.5 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors max-w-xs"
                    >
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                      Additional Info
                    </a>
                  )}
                  
                  {/* Resource links (Federal) */}
                  {opportunity.resourceLinks?.map((link: string, idx: number) => {
                    let displayName = `Resource ${idx + 1}`
                    try {
                      const url = new URL(link)
                      const filename = url.pathname.split('/').filter(Boolean).pop()
                      if (filename && filename.includes('.')) {
                        displayName = decodeURIComponent(filename)
                      }
                    } catch {}
                    return (
                      <a
                        key={`res-${idx}`}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 min-h-[2rem] py-1.5 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors max-w-xs"
                      >
                        <FileText className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{displayName}</span>
                      </a>
                    )
                  })}

                  {/* Notice links (Federal) */}
                  {opportunity.noticeLinks?.map((link: any, idx: number) => {
                    const isApiLink = link.url?.includes('api.sam.gov') || link.href?.includes('api.sam.gov')
                    if (isApiLink) return null

                    const linkUrl = link.href || link.url
                    if (!linkUrl) return null

                    let displayName = `Document ${idx + 1}`
                    if (link.title) {
                      displayName = link.title
                    } else if (link.rel) {
                      displayName = link.rel.replace(/([A-Z])/g, ' $1').replace(/^./, (s: string) => s.toUpperCase()).trim()
                    } else {
                      try {
                        const url = new URL(linkUrl)
                        const filename = url.pathname.split('/').filter(Boolean).pop()
                        if (filename && filename.includes('.')) {
                          displayName = decodeURIComponent(filename)
                        }
                      } catch {}
                    }

                    return (
                      <a
                        key={`not-${idx}`}
                        href={linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 min-h-[2rem] py-1.5 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors max-w-xs"
                      >
                        <FileText className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{displayName}</span>
                      </a>
                    )
                  })}
                </div>
                
                {/* Helper text for documents if we hid some */}
                {opportunity.noticeLinks && opportunity.noticeLinks.some((link: any) => link.url?.includes('api.sam.gov') || link.href?.includes('api.sam.gov')) && (
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Note: Some documents are only accessible directly through the official SAM.gov source link above due to security restrictions.
                  </p>
                )}
              </div>
            )}

            {/* Additional Information */}
            {(opportunity.officeCity || opportunity.primaryNaicsCode || opportunity.classificationCode) && (
              <div className="pt-4 border-t border-border/50">
                <div className="text-sm font-semibold text-foreground mb-3">Additional Information</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {opportunity.officeCity && (
                    <div>
                      <span className="text-muted-foreground">Office City: </span>
                      <span className="font-medium">{opportunity.officeCity}</span>
                    </div>
                  )}
                  {opportunity.primaryNaicsCode && (
                    <div>
                      <span className="text-muted-foreground">Primary NAICS: </span>
                      <span className="font-medium font-mono">{opportunity.primaryNaicsCode}</span>
                    </div>
                  )}
                  {opportunity.classificationCode && (
                    <div>
                      <span className="text-muted-foreground">Classification Code: </span>
                      <span className="font-medium font-mono">{opportunity.classificationCode}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* SLED Contact Information */}
            {contractLevel === 'sled' && (opportunity.contactName || opportunity.contactEmail || opportunity.contactPhone) && (
              <div className="pt-4 border-t border-border/50">
                <div className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Contact Information
                </div>
                <div className="bg-muted/30 rounded-lg p-3 border border-border/50 space-y-1.5">
                  {opportunity.contactName && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="font-semibold">{opportunity.contactName}</span>
                    </div>
                  )}
                  {opportunity.contactEmail && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <a href={`mailto:${opportunity.contactEmail}`} className="text-primary hover:underline break-all">{opportunity.contactEmail}</a>
                    </div>
                  )}
                  {opportunity.contactPhone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <a href={`tel:${opportunity.contactPhone}`} className="text-primary hover:underline">{opportunity.contactPhone}</a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Grant-Specific Details */}
            {contractLevel === 'grants' && (opportunity.awardCeiling || opportunity.awardFloor || opportunity.expectedAwards || opportunity.costSharing || opportunity.eligibleApplicants || opportunity.cfdaNumbers || opportunity.topicNumber || opportunity.topicDescription) && (
              <div className="pt-4 border-t border-border/50">
                <div className="text-sm font-semibold text-foreground mb-3">Grant Details</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {opportunity.awardCeiling && (
                    <div>
                      <span className="text-muted-foreground">Award Ceiling: </span>
                      <span className="font-semibold text-emerald-600">
                        {!isNaN(Number(opportunity.awardCeiling)) 
                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(opportunity.awardCeiling))
                          : opportunity.awardCeiling}
                      </span>
                    </div>
                  )}
                  {opportunity.awardFloor && (
                    <div>
                      <span className="text-muted-foreground">Award Floor: </span>
                      <span className="font-semibold text-emerald-600">
                        {!isNaN(Number(opportunity.awardFloor))
                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(opportunity.awardFloor))
                          : opportunity.awardFloor}
                      </span>
                    </div>
                  )}
                  {opportunity.expectedAwards && (
                    <div>
                      <span className="text-muted-foreground">Expected Awards: </span>
                      <span className="font-medium">{opportunity.expectedAwards}</span>
                    </div>
                  )}
                  {opportunity.costSharing && (
                    <div>
                      <span className="text-muted-foreground">Cost Sharing: </span>
                      <span className="font-medium">{opportunity.costSharing}</span>
                    </div>
                  )}
                  {opportunity.cfdaNumbers && Array.isArray(opportunity.cfdaNumbers) && opportunity.cfdaNumbers.length > 0 && (
                    <div className="col-span-full">
                      <span className="text-muted-foreground">CFDA Numbers: </span>
                      <span className="font-medium font-mono">{opportunity.cfdaNumbers.join(', ')}</span>
                    </div>
                  )}
                  {opportunity.topicNumber && (
                    <div>
                      <span className="text-muted-foreground">Topic Number: </span>
                      <span className="font-medium font-mono">{opportunity.topicNumber}</span>
                    </div>
                  )}
                  {opportunity.program && (
                    <div>
                      <span className="text-muted-foreground">Program: </span>
                      <span className="font-medium">{opportunity.program}</span>
                    </div>
                  )}
                </div>
                {opportunity.topicDescription && (
                  <div className="mt-3">
                    <div className="text-sm text-muted-foreground mb-1">Topic Description</div>
                    <div className="bg-muted/30 rounded-lg p-3 border border-border/50 text-sm leading-relaxed whitespace-pre-wrap">
                      {opportunity.topicDescription}
                    </div>
                  </div>
                )}
                {opportunity.eligibleApplicants && Array.isArray(opportunity.eligibleApplicants) && opportunity.eligibleApplicants.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm text-muted-foreground mb-1">Eligible Applicants</div>
                    <div className="flex flex-wrap gap-1.5">
                      {opportunity.eligibleApplicants.map((a: any, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">{typeof a === 'string' ? a : a.label || a.name || JSON.stringify(a)}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Grantor Contact (Grants) */}
            {contractLevel === 'grants' && (opportunity.grantorContactName || opportunity.grantorContactEmail || opportunity.grantorContactPhone) && (
              <div className="pt-4 border-t border-border/50">
                <div className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Grantor Contact
                </div>
                <div className="bg-muted/30 rounded-lg p-3 border border-border/50 space-y-1.5">
                  {opportunity.grantorContactName && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="font-semibold">{opportunity.grantorContactName}</span>
                    </div>
                  )}
                  {opportunity.grantorContactEmail && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <a href={`mailto:${opportunity.grantorContactEmail}`} className="text-primary hover:underline break-all">{opportunity.grantorContactEmail}</a>
                    </div>
                  )}
                  {opportunity.grantorContactPhone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <a href={`tel:${opportunity.grantorContactPhone}`} className="text-primary hover:underline">{opportunity.grantorContactPhone}</a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Track Button - opens trial modal */}
            <div className="pt-4 border-t border-border/50">
              <Button
                variant="default"
                className="w-full"
                onClick={() => {
                  setIsDetailsModalOpen(false)
                  setTimeout(() => setIsTrialModalOpen(true), 200)
                }}
              >
                <Star className="h-4 w-4 mr-2" />
                Track Opportunity
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Start a free trial to track opportunities and receive notifications
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Free Trial Sign Up Modal */}
      <Dialog open={isTrialModalOpen} onOpenChange={setIsTrialModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-primary/10">
                <Lock className="h-8 w-8 text-primary" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl">
              Start Your Free 14-Day Trial
            </DialogTitle>
            <DialogDescription className="text-center">
              Get full access to this opportunity and thousands more. No credit card required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Opportunity Preview */}
            <div className="p-4 bg-muted/50 rounded-lg border">
              <h4 className="font-semibold text-sm line-clamp-2 mb-2">{displayTitle}</h4>
              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                {formattedValue && (
                  <span className="flex items-center gap-1">
                    <DollarSign className="h-3 w-3 text-emerald-600" />
                    {formattedValue}
                  </span>
                )}
                {formattedDueDate !== 'TBD' && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Due: {formattedDueDate}
                  </span>
                )}
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-2">
              <h5 className="font-semibold text-sm">With your free trial, you&apos;ll get:</h5>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary shrink-0" />
                  AI-powered proposal generation
                </li>
                <li className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-primary shrink-0" />
                  Track & save opportunities
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary shrink-0" />
                  Full opportunity details & documents
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary shrink-0" />
                  Access to {contractLevel === 'sled' ? 'all 50 states' : 'federal opportunities'}
                </li>
              </ul>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-2 pt-2">
              <Button asChild size="lg" className="w-full">
                <Link href="/signup">
                  Start Free Trial
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link href="/login">
                  Already have an account? Log in
                </Link>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
